import { createSelector } from "reselect";
import { type RootState } from "~/app/store";
import { type ResolvedWishlistEntryModel, type WishlistEntryModel } from "~/features/wishlist/types/model";
import { type SelectNetPriceProps } from "~/domains/product/utils/productDescriptorUtils";
import { selectDeepProductDescriptor } from "~/domains/product/redux/selectors";
import { getCanUserAddToCart } from "~/features/productDetails/components/ProductSummary/ExtendedPdpSummary";
import { userIsRegistered } from "~/domains/user/utils/roles";
import { type WishlistPageState } from "~/features/wishlist/redux/types";

export const getWishlists = createSelector(
  (state: WishlistPageState) => state.wishlistIds,
  (state: WishlistPageState) => state.wishlists,
  (ids, wishlistsById) => ids.map((id) => wishlistsById[id]),
);

export const selectIsWishlistsLoading = (state: RootState) => state.wishlistPage.loading || !state.wishlistPage.loaded;

export const selectSelectedWishlistId = (state: RootState) => state.wishlistPage.selectedWishlistId;
export const selectWishlists = (state: RootState) => getWishlists(state.wishlistPage);

export const selectedSelectedWishlist = createSelector(
  selectSelectedWishlistId,
  (state: RootState) => state.wishlistPage.wishlists,
  (wishlistId, wishlists) => {
    if (!wishlistId) {
      return null;
    }
    return wishlists[wishlistId];
  },
);

export const selectSelectedWishlistEntries = createSelector(
  selectedSelectedWishlist,
  (state: RootState) => state.wishlistPage.entries,
  (wishlist, entriesById) => {
    if (!wishlist) {
      return [];
    }
    return wishlist.entryIds.map((id) => entriesById[id]);
  },
);

export const selectWishlistSize = (wishlistId: string) => (state: RootState) => {
  return state.wishlistPage.wishlists[wishlistId]?.entryIds.length ?? 0;
};

export const isAllWishlistDetailsLoaded = createSelector(
  (state: RootState) => state.products.price,
  (priceMap) => {
    return Object.values(priceMap).every((entry) => entry.state === "loaded");
  },
);

export const getSelectPriceParamsFromWishlistEntry = (
  wishlistEntry: WishlistEntryModel | ResolvedWishlistEntryModel,
  packCode: string,
): Required<SelectNetPriceProps> => {
  return {
    packCode,
    extraQuantity: wishlistEntry.quantity,
    priceReference: wishlistEntry.priceReference,
    productCode: "productCode" in wishlistEntry ? wishlistEntry.productCode : wishlistEntry.product.base.code,
  };
};

export const selectWishlistAmount = (state: RootState) =>
  state.wishlistPage.loaded ? state.wishlistPage.wishlistIds.length : undefined;

export const selectFullWishlist = createSelector(
  (state: RootState, wishlistId: string) => state.wishlistPage?.wishlists[wishlistId],
  (state: RootState, wishlistId: string) => state.wishlistPage?.entries,
  (state: RootState, _: string) => state.products,
  (wishlistModel, entriesById, productStore) => {
    if (!wishlistModel) {
      return null;
    }
    return {
      ...wishlistModel,
      entries: wishlistModel.entryIds.map((id) => {
        const entry = entriesById[id];
        const product = selectDeepProductDescriptor(productStore, entry);
        return {
          ...entry,
          product,
          isSelectable: userIsRegistered()
            ? true
            : product.isLoaded && getCanUserAddToCart(product, product?.availability?.packagingInformation[0]),
        };
      }),
    };
  },
);
